import React, { useRef, useEffect, useState } from 'react';
import './SendLoanForm.scss';

import { Icon, TextInput, InputAutocompleteAddress } from '../../../../shared/components/ui/atoms';
import { SendLoanFormSubmitted } from '../SendLoanFormSubmitted/SendLoanFormSubmitted';
import { formatPhoneNumber } from '../../../borrower/components';
import { getDisabledStateCodes } from '../../../../utils/constants';
import { formatAddressWithoutCountry } from '../../../../utils/functions';
import { API_URL_GRAPHQL } from '@common/config/environments';


// Extend Window interface for heap
declare global {
  interface Window {
    heap?: {
      identify: (id: string) => void;
      addUserProperties: (properties: any) => void;
    };
  }
}

interface SendLoanFormProps {
  isSelected?: boolean;
  setBottomValue?: (value: number) => void;
}

export const SendLoanForm = (props: SendLoanFormProps) => {
  const { isSelected, setBottomValue } = props;

  return (
    <div {...{ isSelected, setBottomValue }} className='step-section form'>
      <SendLoanFormInner />
    </div>
  );
}

export const sendLeadData = async (data: any) => {
  try {
    let response;
    response = await fetch(`${API_URL_GRAPHQL}/lead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...data,
        urlParams: window.location.search
      })
    });


    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const responseData = await response.json();

    return responseData;
  } catch (error) {
    console.error('Error:', error);
  }
}

interface SendLoanFormInnerProps {
  data?: any;
}

export const SendLoanFormInner = (props: SendLoanFormInnerProps) => {
  const [formSubmitted, setFormSubmitted] = useState(false)

  const onSubmission = async (formData: any) => {
    await new Promise(res => setTimeout(res, 2000))
    const responseData = await sendLeadData({ ...formData, ...props.data })
    setFormSubmitted(true)
    try {
      const heap = window.heap
      if (heap && heap.identify && heap.addUserProperties) {
        if (responseData?._id) {
          heap.identify(responseData._id);
          heap.addUserProperties({ email: responseData.email });
        }
      }
    } catch (e) {

    }
  }

  return (
    <>
      {
        !formSubmitted
          ?
          <SendLoanFormContent {...props} onSubmission={onSubmission} />
          :
          <SendLoanFormSubmitted />
      }
    </>
  )
}

interface StepSectionWrapperProps {
  isSelected?: boolean;
  setBottomValue?: (value: number) => void;
  children?: React.ReactNode;
}

const StepSectionWrapper = (props: StepSectionWrapperProps) => {
  const { isSelected, setBottomValue, children } = props;

  const stepRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (stepRef.current && isSelected) {
      setBottomValue?.(stepRef.current.offsetTop)
    }
  }, [isSelected, stepRef.current])

  return (
    <div className='step-section' ref={stepRef}>
      {children}
    </div>
  );
}



interface SendLoanFormContentProps {
  onSubmission?: (formData: any) => void;
  title?: string;
}

export const SendLoanFormContent = (props: SendLoanFormContentProps) => {
  const { onSubmission, title } = props

  const [name, setName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [email, setEmail] = useState('')
  const [investmentAddress, setInvestmentAddress] = useState('')
  const [investmentAddressFormatted, setInvestmentAddressFormatted] = useState('')
  const [comments, setComments] = useState('')
  const [errors, setErrors] = useState<Record<string, string | null>>({})
  const [touched, setTouched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isAddressSelected, setIsAddressSelected] = useState(false)
  const [selectedAddressDisplay, setSelectedAddressDisplay] = useState('')

  const validateInput = (label: string, value: string) => {
    switch (label) {
      case 'Name':
        return value.trim() === '' ? 'This field is required' : null;
      case 'Phone number':
        if (value.trim() === '') return 'This field is required';
        const digits = value.replace(/\D/g, '');
        if (digits.length < 10) return 'Phone number must have 10 digits';
        return null;
      case 'Email address':
        const emailRegex = /\S+@\S+\.\S+/;
        return !emailRegex.test(value) ? 'Invalid email address' : null;
      case "Investment Address":
        if (value.trim() === "") return "This field is required";
        if (!isAddressSelected) return "Please select an address from the list";
        return null;
      default:
        return null;
    }
  }

  const checkErrors = () => {
    const newErrors: Record<string, string | null> = {
      name: validateInput('Name', name),
      phoneNumber: validateInput('Phone number', phoneNumber),
      email: validateInput('Email address', email),
      investmentAddress: validateInput('Investment Address', investmentAddress),
    };
    setErrors(newErrors);
    return newErrors;
  }

  const handleSubmission = async () => {
    setTouched(true);
    const errors = checkErrors();
    const hasErrors = Object.values(errors).some(error => error !== null);

    if (!hasErrors) {
      setIsLoading(true)
      await onSubmission?.({ name, phoneNumber, email: typeof email === 'string' ? email.trim().toLowerCase() : email, investmentAddress: investmentAddressFormatted || investmentAddress, comments });
      setIsLoading(false)
    }
  }

  const handleChange = (
    setter: (value: string) => void,
    errorKey: keyof typeof errors,
    validationLabel: string
  ) => (value: any) => {
    const nextValue = value && value.target !== undefined ? value.target.value : value;
    setter(nextValue ?? '');

    if (touched) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [errorKey]: validateInput(validationLabel, nextValue ?? ''),
      }));
    }
  };

  const handleInvestmentAddressChange = (value: any) => {
    const nextValue = value && value.target !== undefined ? value.target.value : value;
    const normalizedValue = (nextValue ?? '').toString();

    setInvestmentAddress(normalizedValue);

    // If the user edits/clears the input after selecting an address, require a new selection.
    const matchesLastSelection =
      normalizedValue.trim() !== '' && normalizedValue === selectedAddressDisplay;

    if (!matchesLastSelection) {
      setIsAddressSelected(false);
      setInvestmentAddressFormatted('');
    }

    if (touched) {
      setErrors(prevErrors => ({
        ...prevErrors,
        investmentAddress: validateInput('Investment Address', normalizedValue),
      }));
    }
  };

  return (
    <div className='send-loan-form'>
      <div className='info'>
        <h1 className='title'>
          {title ?? "We can’t automatically price this loan"}
        </h1>
        <h3 className='description'>
          Enter your contact information below and one of our loan officers will reach out with custom loan options
        </h3>

        <label htmlFor='name-input'>Name</label>
        <input
          id='name-input'
          placeholder='Enter your name'
          type='text'
          value={name}
          onChange={handleChange(setName, 'name', 'Name')}
        />
        <p className='error'>{errors.name}</p>

        <label htmlFor='phone-input'>Phone number</label>
        <input
          id='phone-input'
          placeholder='Enter your phone number'
          type='text'
          value={phoneNumber}
          onChange={(e) => handleChange(setPhoneNumber, 'phoneNumber', 'Phone number')(formatPhoneNumber(e.target.value))}
        />
        <p className='error'>{errors.phoneNumber}</p>

        <label htmlFor='email-input'>Email address</label>
        <input
          id='email-input'
          placeholder='Enter your email address'
          type='text'
          value={email}
          onChange={handleChange(setEmail, 'email', 'Email address')}
        />
        <p className='error'>{errors.email}</p>

        <label htmlFor='investment-address-input'>Investment property address</label>
        <InputAutocompleteAddress
          value={investmentAddress}
          onChange={handleInvestmentAddressChange}
          onSelect={(data: any) => {
            if (!data || !data.formatted_address) {
              setErrors(prevErrors => ({
                ...prevErrors,
                investmentAddress: 'This field is required',
              }));
              setIsAddressSelected(false);
              setSelectedAddressDisplay('');
              setInvestmentAddressFormatted('');
              return;
            }

            const selectedAddress = data.selectedAddress || data.formatted_address;
            const displayAddress = formatAddressWithoutCountry(selectedAddress);
            setInvestmentAddress(displayAddress);
            setSelectedAddressDisplay(displayAddress);
            setInvestmentAddressFormatted(formatAddressWithoutCountry(data.formatted_address));
            setIsAddressSelected(true);
            if (touched) {
              setErrors(prevErrors => ({
                ...prevErrors,
                investmentAddress: null,
              }));
            }
          }}
          excludedStates={getDisabledStateCodes()}
        />
        <p className='error'>{errors.investmentAddress}</p>

        <label htmlFor='comments-input'>Comments</label>
        <textarea
          placeholder='Other information that can help our loan officers get you the best rate'
          value={comments}
          onChange={handleChange(setComments, 'comments', 'Comments')}
        />

        <div className='loan-options-button' onClick={isLoading ? () => { } : () => handleSubmission()}>
          Contact me with loan options
        </div>
        <p className='caption'>
          We will NEVER sell your info.<br></br>
          By providing your phone and email, you agree to receive text messages from Constitution Lending.
        </p>
      </div>
    </div>
  );
}